import { useLanguageStore } from '@/helpers/composables/useStores';
import {
	GetSingleQtvSimilarTitlesDocument,
	GetSingleQtvSimilarTitlesQuery,
	GetSingleQtvSimilarTitlesQueryVariables,
} from '@/components/buybox-new/qualityTV/queries/GetSingleQTV.query';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed } from 'vue';
import { SimilarTitlesSorting } from '@/@types/graphql-types';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useQuery } from '@/helpers/composables/useApollo';

export function useSingleQTV(
	title: MaybeRefOrGetter<TitleDetail>,
	providerShortName: MaybeRefOrGetter<string>,
	enabled: MaybeRefOrGetter<boolean>
) {
	const { country, language } = useLanguageStore();
	const _title = toValue(title);
	const variables = computed<GetSingleQtvSimilarTitlesQueryVariables>(() => ({
		country: country.value,
		language: language.value,
		first: 1,
		titleId: _title.__typename === 'Season' ? _title.show.id : _title.id,
		filters: {
			packages: [toValue(providerShortName)],
		},
		offerFilters: {
			packages: [toValue(providerShortName)],
			bestOnly: true,
		},
		sorting: SimilarTitlesSorting['ImdbScore'],
	}));

	const { result, loading, onError } = useQuery<
		GetSingleQtvSimilarTitlesQuery,
		GetSingleQtvSimilarTitlesQueryVariables
	>(GetSingleQtvSimilarTitlesDocument, variables, { enabled: toValue(enabled) });

	onError(error => {
		captureMessageForSentry('[Graphql GetSingleQtvSimilarTitlesQuery]', {
			where: '[SingleQTV.vue]',
			error,
		});
	});

	const similarTitle = computed(() =>
		result.value?.node?.__typename === 'Movie' || result.value?.node?.__typename === 'Show'
			? result?.value?.node?.similarTitlesV2.edges?.[0]
			: null
	);

	const isSQTVEmpty = computed(() => {
		if (loading.value) return false;

		return similarTitle.value == null;
	});

	return {
		similarTitle,
		loading,
		isSQTVEmpty,
	};
}
